import React, { useState, useEffect, useRef } from 'react';
import { onConversationActivity } from '../../../lib/conversationRealtime';
import { formatInTenantTz } from '../../../lib/datetime';
import { isMeaningfulResolution } from '../../../lib/solutionResolution';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import {
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  MessageSquare,
  User,
  ArrowRight,
  RefreshCw,
  UserPlus,
  Send,
  Phone
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { auth } from '../../../lib/firebase';
import { canManageEscalations } from '../../../lib/role';

const EscalationManager = () => {
  const { t } = useLanguage();
  const [escalations, setEscalations] = useState([]);
  // Callbacks come from the CANONICAL db.callbacks store (GET /api/v1/callbacks/business/{id}),
  // NOT from db.escalations pending_callback — so callbacks scheduled in Unified Desktop appear here.
  const [callbacks, setCallbacks] = useState([]);
  // Tracks the in-flight callback call so its "Call Now" button shows a loading state.
  const [callingCbId, setCallingCbId] = useState(null);
  // Live clock (ms) that drives Due Soon / Overdue callback reminders; ticks every 30s.
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Callback ids already reminded this session (one in-app reminder per callback, no spam).
  const remindedRef = useRef(new Set());
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [viewEscDialog, setViewEscDialog] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [resolveDialog, setResolveDialog] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [submitToSolutionBank, setSubmitToSolutionBank] = useState(false);
  // GOV-7 WS1 Area D — server-authoritative personal buckets from GET /escalations/me. Display tabs read
  // these directly (count↔list parity) instead of re-deriving from a flattened list with a non-existent
  // 'pending' status.
  const [buckets, setBuckets] = useState({ pending: [], assigned_to_me: [], resolved: [] });
  // assignInputs: { [escId]: staffId } — populated by staff Select
  const [assignInputs, setAssignInputs] = useState({});
  // D15 — management Team / Unassigned queues, server-authoritative via GET /escalations/team.
  // Never derived by client-side filtering of all data; the backend scope param owns the projection.
  const [teamEscalations, setTeamEscalations] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  // reassignInputs: { [escId]: staffId } for the management reassign Select
  const [reassignInputs, setReassignInputs] = useState({});
  // The escalation id with an in-flight management mutation (assign/reassign/close) — disables its row.
  const [actioningId, setActioningId] = useState(null);
  // Per-card inline message state — keyed by escalation ID
  const [msgTexts, setMsgTexts] = useState({});
  const [msgSending, setMsgSending] = useState({});
  // Detail dialog conversation thread + message compose
  const [convMessages, setConvMessages] = useState([]);
  const [convMessagesLoading, setConvMessagesLoading] = useState(false);
  const [detailMsgText, setDetailMsgText] = useState('');
  const [detailMsgSending, setDetailMsgSending] = useState(false);
  // Local staff roster — fetched directly so this component doesn't depend on
  // BusinessContext async timing. Same endpoint as StaffManagement / BusinessContext.
  const [staffRoster, setStaffRoster] = useState([]);
  const { toast } = useToast();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { currentBusiness, getBusinessStaff } = useBusiness();
  const { user } = useAuth();

  // D15 — escalation management authority (shared canonical helper lib/role.js canManageEscalations):
  // administrator + group_administrator collapse to 'admin'; director/executive stay management; super_admin
  // excluded. Backend remains the authority. Declared HERE (right after `user`) because it is referenced by
  // the management-queue effect's dependency array below — a dependency array is evaluated DURING render, so
  // a later `const` threw a temporal-dead-zone ReferenceError ("Cannot access before initialization") that
  // white-paged the whole route.
  const isAdminOrManager = canManageEscalations(user?.role);

  // Merge: prefer directly-fetched roster; fall back to BusinessContext if not yet loaded.
  const businessStaff = staffRoster.length > 0 ? staffRoster : (getBusinessStaff() || []);

  // Use auth.currentUser.phoneNumber (live Firebase claim) as primary phone source.
  // crm_user_data may lack phoneNumber/email for sessions established before those fields
  // were added to verifyOTP — auth.currentUser is always populated for Phone Auth users.
  const _normPhone = (p) => (p || '').replace(/\D/g, '');
  const _firebasePhone = auth.currentUser?.phoneNumber || '';
  const currentStaff = businessStaff.find(s => {
    const uPhone = _normPhone(_firebasePhone || user?.phoneNumber || user?.phone);
    const uEmail = (user?.email || '').toLowerCase().trim();
    return (uPhone && _normPhone(s.phone) === uPhone) ||
           (uEmail && (s.email || '').toLowerCase().trim() === uEmail);
  });
  const currentStaffId = currentStaff?.id || null;

  useEffect(() => {
    fetchEscalations();
    fetchCallbacks();
  }, [activeTab]);

  // Callbacks tab source of truth: canonical db.callbacks (operator-scoped by the backend).
  const fetchCallbacks = async () => {
    const businessId = currentBusiness?.brandId;
    if (!businessId) return;
    try {
      // scope=me → server returns only callbacks assigned to / created by THIS staff member
      // (My Escalations is personal; team/tenant callbacks belong to a supervisory view).
      const res = await makeAuthenticatedRequest(`/api/v1/callbacks/business/${businessId}?scope=me`);
      if (res.ok) {
        const data = await res.json();
        setCallbacks(data.callbacks || []);
      }
    } catch (e) {
      // non-fatal: leave prior list; escalation tabs are unaffected
    }
  };

  // Fetch staff roster directly — same /billing/staff/by-brand endpoint as StaffManagement.
  // Independent of BusinessContext timing so staff is always available for self-assignment.
  useEffect(() => {
    const loadStaff = async () => {
      const brandId = currentBusiness?.brandId;
      if (!brandId) return;
      try {
        const res = await makeAuthenticatedRequest(
          `/api/v1/billing/staff/by-brand/${brandId}`
        );
        if (res.ok) {
          const data = await res.json();
          setStaffRoster(data.staff || []);
        }
      } catch (e) {}
    };
    loadStaff();
  }, [currentBusiness?.brandId]);

  // Fetch from escalations endpoint — returns records with .id = escalation UUID
  const fetchEscalations = async () => {
    setLoading(true);
    const businessId = currentBusiness?.brandId;
    if (!businessId) { setLoading(false); return; }
    try {
      // Server-authoritative PERSONAL scope: /escalations/me returns only this staff member's
      // pending / assigned_to_me / resolved records (staff derived from the token, never the
      // client). Truly unassigned tenant escalations are excluded here by design. Flatten into
      // the status-based tab model — the tabs are now personal, not tenant-wide.
      const response = await makeAuthenticatedRequest(`/api/v1/escalations/me`);
      if (response.ok) {
        const data = await response.json();
        // GOV-7 WS1 Area D — TRUST the server-authoritative buckets. The backend already scopes to THIS
        // staff member and to the CANONICAL escalation states (pending = assigned-but-not-accepted +
        // callbacks; assigned_to_me = receiving_attention; resolved = resolved/closed). The old code
        // flattened these and re-derived tabs client-side with `status === 'pending'` (a status that does
        // not exist) AND a fragile `assigned_to === currentStaffId` match — so "Pending" pinned at 0.
        const _p = data.pending || [];
        const _a = data.assigned_to_me || [];
        const _r = data.resolved || [];
        setBuckets({ pending: _p, assigned_to_me: _a, resolved: _r });
        setEscalations([..._p, ..._a, ..._r]);  // union retained only for resolveCallbackConvId lookup
      } else {
        throw new Error(`Server returned ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching escalations:', error);
      setEscalations([]);
      toast({
        title: t('ops.esc.failedLoadEsc'),
        description: error.message || t('ops.esc.checkConnection'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // `live` refreshes in place: blanking the list and showing a spinner on every incoming message
  // would make the thread flicker on each turn of a normal conversation.
  const fetchConversationMessages = async (conversationId, { live = false } = {}) => {
    if (!conversationId) return;
    if (!live) {
      setConvMessagesLoading(true);
      setConvMessages([]);
    }
    try {
      const res = await makeAuthenticatedRequest(`/api/v1/conversations/${conversationId}/messages`);
      if (res.ok) {
        const data = await res.json();
        setConvMessages(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Conversation messages fetch failed', e);
    } finally {
      setConvMessagesLoading(false);
    }
  };

  // Send a customer-visible operator message on the escalation's conversation thread.
  // conversation_id comes from escalation or ticket — same shared ID created at escalation time.
  // message_type: "operator" — visible to customer; filtered from internal notes.
  const handleSendMessage = async (esc, text, escId, isDetailDialog = false) => {
    if (!text?.trim()) return;
    const convId = esc.conversation_id || esc.ticket_id;
    if (!convId) {
      toast({ title: t('ops.esc.noConvLinkedTitle'), description: t('ops.esc.noConvLinkedDesc'), variant: 'destructive' });
      return;
    }
    if (isDetailDialog) {
      setDetailMsgSending(true);
    } else {
      setMsgSending(prev => ({ ...prev, [escId]: true }));
    }
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/conversations/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: convId,
          sender_id: currentStaffId || user?.id || user?.uid || '',
          sender_type: 'operator',
          message: text.trim(),
          message_type: 'operator',
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Send failed (HTTP ${resp.status})`);
      }
      if (isDetailDialog) {
        setDetailMsgText('');
        // Refresh thread in dialog
        fetchConversationMessages(convId);
      } else {
        setMsgTexts(prev => ({ ...prev, [escId]: '' }));
      }
      toast({ title: t('ops.esc.messageSent'), description: t('ops.esc.messageSentPush') });
    } catch (err) {
      toast({ title: t('ops.esc.sendFailed'), description: err?.message || t('ops.esc.failedSendMessage'), variant: 'destructive' });
    } finally {
      if (isDetailDialog) {
        setDetailMsgSending(false);
      } else {
        setMsgSending(prev => ({ ...prev, [escId]: false }));
      }
    }
  };

  const fetchTranscript = async (sessionId) => {
    if (!sessionId) return;
    setTranscriptLoading(true);
    setTranscript(null);
    try {
      const res = await makeAuthenticatedRequest(`/api/v1/ai-voice/session/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        setTranscript(data.session?.conversation_history || data.conversation_history || []);
      }
    } catch (e) {
      console.error('Transcript fetch failed', e);
    } finally {
      setTranscriptLoading(false);
    }
  };

  // LIVE THREAD UPDATES. The backend broadcasts `new_message` on the business socket, but Web had
  // no consumer for it, so a customer reply only appeared once some other action refetched the
  // thread — usually the operator sending another message. The socket carries a SIGNAL only: we
  // refetch from the API, so db.messages remains the single durable authority (Bedrock Rule 2) and
  // a duplicated or out-of-order frame cannot corrupt what is displayed.
  const openConvIdRef = useRef(null);
  useEffect(() => {
    openConvIdRef.current = viewEscDialog
      ? (viewEscDialog.conversation_id || viewEscDialog.ticket_id)
      : null;
  }, [viewEscDialog]);

  useEffect(() => onConversationActivity(({ conversationId }) => {
    if (conversationId && conversationId === openConvIdRef.current) {
      fetchConversationMessages(conversationId, { live: true });
    }
  }), []);

  const openEscalationDetail = (esc) => {
    setViewEscDialog(esc);
    setTranscript(null);
    setConvMessages([]);
    setDetailMsgText('');
    if (esc.session_id) {
      fetchTranscript(esc.session_id);
    }
    const convId = esc.conversation_id || esc.ticket_id;
    if (convId) {
      fetchConversationMessages(convId);
    }
  };

  // Call customer from Escalation Manager detail dialog.
  // Uses POST /conversations/{id}/call — writes call_event to conversation thread.
  // The escalation always has conversation_id or ticket_id which aliases to a conversation.
  const handleCallFromEscalation = async (esc) => {
    const convId = esc?.conversation_id || esc?.ticket_id;
    const customerName = esc?.customer_name || t('ops.common.customer');
    if (!convId) {
      toast({ title: t('ops.common.cannotCall'), description: t('ops.esc.noLinkedConvEsc'), variant: 'destructive' });
      return;
    }
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/conversations/${convId}/call`, { method: 'POST' });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        const initiated = data.status === 'initiated';
        toast({
          title: initiated ? t('ops.common.callInitiated') : t('ops.common.callLogged'),
          description: data.message || `${initiated ? t('ops.common.calling') : t('ops.common.callRecordedFor')} ${customerName}`,
        });
        // Refresh conversation thread so call_event appears immediately
        fetchConversationMessages(convId);
      } else {
        throw new Error(data.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      toast({ title: t('ops.common.callFailed'), description: err.message, variant: 'destructive' });
    }
  };

  // Resolve the conversation a scheduled callback maps to, WITHOUT guessing. Order:
  // 1) callback.conversation_id, 2) callback.ticket_id (backend /call accepts either),
  // 3) a same-customer escalation already loaded (its conversation is the customer's One-Thread).
  // Returns null when nothing safely maps → caller shows an honest "no conversation" message.
  const resolveCallbackConvId = (cb) => {
    if (cb?.conversation_id) return cb.conversation_id;
    if (cb?.ticket_id) return cb.ticket_id;
    if (cb?.customer_id) {
      const match = escalations.find(
        (e) => e?.customer_id && e.customer_id === cb.customer_id && (e.conversation_id || e.ticket_id));
      if (match) return match.conversation_id || match.ticket_id;
    }
    return null;
  };

  // Call the customer from a scheduled callback card using the SAME real Servisfy VoIP/Twilio route
  // as escalations (POST /conversations/{id}/call — writes a real call_event, dials via Twilio).
  // Success only on backend 2xx; no fake success, no local-only event.
  const handleCallFromCallback = async (cb) => {
    const convId = resolveCallbackConvId(cb);
    const customerName = cb?.customer_display_name || cb?.customer_name || t('ops.common.customer');
    if (!convId) {
      toast({ title: t('ops.common.cannotCall'), description: t('ops.esc.noConvForCallback'), variant: 'destructive' });
      return;
    }
    setCallingCbId(cb.id);
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/conversations/${convId}/call`, { method: 'POST' });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        const initiated = data.status === 'initiated';
        toast({
          title: initiated ? t('ops.common.callInitiated') : t('ops.common.callLogged'),
          description: data.message || `${initiated ? t('ops.common.calling') : t('ops.common.callRecordedFor')} ${customerName}`,
        });
      } else {
        throw new Error(data.detail || `HTTP ${resp.status}`);
      }
    } catch (err) {
      toast({ title: t('ops.common.callFailed'), description: err.message, variant: 'destructive' });
    } finally {
      setCallingCbId(null);
    }
  };

  // ── Scheduled-callback reminders (Web-only, computed from existing fields) ──
  const REMIND_WINDOW_MIN = 5;

  // Parse a callback's scheduled instant from stored date+time. The backend stores no timezone,
  // so this is interpreted in the operator's local time (the view they scheduled it in). Returns
  // ms epoch, or null when not parseable (then no Due Soon/Overdue is asserted).
  const parseCallbackWhen = (cb) => {
    if (!cb?.scheduled_date) return null;
    const tm = (cb.scheduled_time && /^\d{1,2}:\d{2}/.test(cb.scheduled_time)) ? cb.scheduled_time : '00:00';
    const d = new Date(`${cb.scheduled_date}T${tm}`);
    return isNaN(d.getTime()) ? null : d.getTime();
  };

  // Derive a callback's display state. Terminal statuses win; otherwise compute from time.
  const callbackState = (cb) => {
    const raw = (cb?.status || '').toLowerCase();
    if (['completed', 'done', 'resolved'].includes(raw)) return { key: 'completed' };
    if (['cancelled', 'canceled'].includes(raw)) return { key: 'cancelled' };
    const when = parseCallbackWhen(cb);
    if (when == null) return { key: raw === 'overdue' ? 'overdue' : 'scheduled', minsToGo: null };
    const minsToGo = (when - nowMs) / 60000;
    if (minsToGo <= 0) return { key: 'overdue', minsToGo };
    if (minsToGo <= REMIND_WINDOW_MIN) return { key: 'due_soon', minsToGo };
    return { key: 'scheduled', minsToGo };
  };

  const CB_BADGE = {
    scheduled: { label: 'ops.esc.cbScheduled', variant: 'outline', cls: '' },
    due_soon:  { label: 'ops.esc.cbDueSoon',   variant: 'default', cls: 'bg-amber-500 hover:bg-amber-500' },
    overdue:   { label: 'ops.esc.cbOverdue',   variant: 'destructive', cls: '' },
    completed: { label: 'ops.esc.cbCompleted', variant: 'secondary', cls: '' },
    cancelled: { label: 'ops.esc.cbCancelled', variant: 'outline', cls: 'opacity-70' },
  };

  // Tick the clock so Due Soon / Overdue re-evaluate without a manual refresh.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Fire a one-time in-app reminder as each callback enters the 5-minute window. Optional browser
  // notification ONLY if permission was already granted (never prompts). No DB write, no auto-call.
  useEffect(() => {
    callbacks.forEach((cb) => {
      if (!cb?.id || callbackState(cb).key !== 'due_soon') return;
      if (remindedRef.current.has(cb.id)) return;
      remindedRef.current.add(cb.id);
      const name = cb.customer_display_name || cb.customer_name || t('ops.common.customer');
      const body = `${t('ops.esc.cbDueSoonMsg')}: ${name}`;
      toast({ title: t('ops.esc.cbReminderTitle'), description: body });
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          new Notification(t('ops.esc.cbReminderTitle'), { body });
        }
      } catch (_) { /* browser notification best-effort */ }
    });
  }, [nowMs, callbacks]);

  // FIX Issue 3: Accept & Handle is a two-step operation when status='pending'.
  // Backend contract: PUT /accept requires status='assigned'.
  // AI creates escalations at status='pending'. Operator must self-assign first.
  // Step 1 (if pending): POST /assign with {assigned_to: current user id} → status becomes 'assigned'
  // Step 2: PUT /accept → status becomes 'receiving_attention'
  // If already 'assigned', skip step 1 and call accept directly.
  // Admin/manager role required for step 1 (self-assign). Operators will see a clear 403 error.
  const handleAcceptEscalation = async (esc) => {
    const id = esc?.id;
    if (!id) {
      toast({ title: t('common.error'), description: t('ops.esc.cannotResolveEscId'), variant: 'destructive' });
      return;
    }
    try {
      // Step 1: self-assign if escalation is still pending
      if (esc.status === 'pending') {
        if (!currentStaffId) {
          toast({ title: t('ops.esc.cannotSelfAssign'), description: t('ops.esc.cannotSelfAssignDesc'), variant: 'destructive' });
          return;
        }
        const assignResp = await makeAuthenticatedRequest(`/api/v1/escalations/${id}/assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assigned_to: currentStaffId }),
        });
        if (!assignResp.ok) {
          const err = await assignResp.json().catch(() => ({}));
          throw new Error(err.detail || `Failed to claim escalation (HTTP ${assignResp.status}). Admin or Manager role may be required.`);
        }
      }

      // Step 2: accept
      const acceptResp = await makeAuthenticatedRequest(`/api/v1/escalations/${id}/accept`, { method: 'PUT' });
      if (!acceptResp.ok) {
        const err = await acceptResp.json().catch(() => ({}));
        throw new Error(err.detail || `Accept failed (HTTP ${acceptResp.status})`);
      }

      toast({ title: t('ops.esc.escAccepted'), description: t('ops.esc.statusReceivingAttention') });
      fetchEscalations();
    } catch (err) {
      toast({ title: t('common.error'), description: err?.message || t('ops.esc.failedAcceptEsc'), variant: 'destructive' });
    }
  };

  const openResolveDialog = (esc) => {
    setResolveDialog(esc);
    setResolveNotes('');
    setSubmitToSolutionBank(false);
  };

  const handleResolveEscalation = async () => {
    const esc = resolveDialog;
    const id = esc?.id;
    if (!id) return;
    // SLICE A — the operational resolve may proceed with a generic note, but if the operator chose to
    // submit this to the Solution Bank it MUST be a meaningful resolution (mirrors the backend gate).
    if (submitToSolutionBank && !isMeaningfulResolution(resolveNotes)) {
      toast({
        title: t('sb.toast.missingInfo'),
        description: 'Enter a meaningful resolution (not empty or placeholder) to submit it to the Solution Bank.',
        variant: 'destructive',
      });
      return;
    }
    const resolution = resolveNotes.trim() || 'Resolved by operator';
    try {
      // GOV-7 WS1 Area A — the reusable-knowledge submission goes through the ONE canonical backend path
      // (PUT /escalations/{id}/resolve with submit_to_solution_bank), which creates the candidate via
      // create_pending_solution and stamps conversation_id + escalation_id + resolve_event_id. The prior
      // second, direct POST /api/v1/solutions used source:'human_resolution' and dropped the conversation
      // linkage entirely — a journey-sourced candidate with null conversation_id → 422 at manager approval.
      const resp = await makeAuthenticatedRequest(`/api/v1/escalations/${id}/resolve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resolution,
          notes: resolveNotes,
          ...(submitToSolutionBank && resolveNotes.trim() ? {
            submit_to_solution_bank: true,
            solution_title: esc.reason?.substring(0, 80) || 'Escalation Resolution',
          } : {}),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Resolve failed (HTTP ${resp.status})`);
      }

      if (submitToSolutionBank && resolveNotes.trim()) {
        toast({
          title: t('ops.esc.escResolved'),
          description: t('ops.esc.resolutionSubmitted'),
        });
      } else {
        toast({ title: t('ops.esc.escResolved'), description: t('ops.esc.statusResolved') });
      }

      setResolveDialog(null);
      fetchEscalations();
    } catch (err) {
      toast({ title: t('common.error'), description: err?.message || t('ops.esc.failedResolveEsc'), variant: 'destructive' });
    }
  };

  // FIX Issue 3 + 4: Assign uses esc.id. staffId comes from the Select dropdown (staff.id).
  const handleAssignEscalation = async (esc, staffId) => {
    const id = esc?.id;
    if (!id || !staffId) return;
    try {
      await makeAuthenticatedRequest(`/api/v1/escalations/${id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: staffId }),
      });
      toast({ title: t('ops.esc.escAssigned') });
      setAssignInputs(prev => ({ ...prev, [id]: '' }));
      fetchEscalations();
    } catch (err) {
      toast({ title: t('common.error'), description: err?.message || t('ops.esc.failedAssignEsc'), variant: 'destructive' });
    }
  };

  // D15 — server-authoritative management queue. scope=team → tenant-wide; scope=unassigned → the
  // backend's authorized unassigned projection. NEVER a client-side "fetch all + filter" derivation.
  // Management-only; guarded here as defense-in-depth (backend independently 403s a non-manager).
  const fetchTeam = async (scope) => {
    if (!isAdminOrManager) return;
    setTeamLoading(true);
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/escalations/team?scope=${encodeURIComponent(scope)}`);
      if (!resp.ok) {
        if (resp.status === 403) { setTeamEscalations([]); return; }
        throw new Error(`HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setTeamEscalations(Array.isArray(data.escalations) ? data.escalations : []);
    } catch (e) {
      setTeamEscalations([]);
      toast({ title: t('common.error'), description: t('ops.esc.failedLoadTeam'), variant: 'destructive' });
    } finally {
      setTeamLoading(false);
    }
  };

  // Refresh the management queue for whichever management tab is showing (used after a mutation).
  const refreshTeam = () => {
    if (activeTab === 'unassigned') fetchTeam('unassigned');
    else if (activeTab === 'team') fetchTeam('team');
  };

  const handleReassignEscalation = async (esc, staffId) => {
    const id = esc?.id;
    if (!id || !staffId || staffId === '_none') return;
    setActioningId(id);
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/escalations/${id}/reassign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: staffId }),
      });
      if (!resp.ok) {
        // Honest conflict handling — never surface raw backend text; refresh on stale state / lost access.
        const safe = resp.status === 403 ? t('ops.esc.reassignNotAllowed')
          : resp.status === 409 || resp.status === 422 ? t('ops.esc.escStateChanged')
          : t('ops.esc.failedReassign');
        toast({ title: t('common.error'), description: safe, variant: 'destructive' });
        refreshTeam();
        return;
      }
      toast({ title: t('ops.esc.escReassigned') });
      setReassignInputs(prev => ({ ...prev, [id]: '' }));
      refreshTeam();
    } catch (e) {
      toast({ title: t('common.error'), description: t('ops.esc.failedReassign'), variant: 'destructive' });
    } finally {
      setActioningId(null);
    }
  };

  const handleCloseEscalation = async (esc) => {
    const id = esc?.id;
    if (!id) return;
    setActioningId(id);
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/escalations/${id}/close`, { method: 'PUT' });
      if (!resp.ok) {
        // 422 = not resolved yet (Close is NOT a substitute for Resolve); 403 = not authorized.
        const safe = resp.status === 403 ? t('ops.esc.closeNotAllowed')
          : resp.status === 422 ? t('ops.esc.mustResolveBeforeClose')
          : resp.status === 409 ? t('ops.esc.escStateChanged')
          : t('ops.esc.failedClose');
        toast({ title: t('common.error'), description: safe, variant: 'destructive' });
        refreshTeam();
        return;
      }
      toast({ title: t('ops.esc.escClosed') });
      refreshTeam();
    } catch (e) {
      toast({ title: t('common.error'), description: t('ops.esc.failedClose'), variant: 'destructive' });
    } finally {
      setActioningId(null);
    }
  };

  // D15 — load the server-authoritative management queue whenever a management tab becomes active.
  useEffect(() => {
    if (activeTab === 'team') fetchTeam('team');
    else if (activeTab === 'unassigned') fetchTeam('unassigned');
  }, [activeTab, isAdminOrManager]); // fetchTeam intentionally omitted — stable per render, avoids refetch loop

  const handleViewEscalation = (esc) => {
    if (!esc?.id) {
      toast({ title: t('ops.esc.noEscId'), description: t('ops.esc.noEscIdDesc'), variant: 'destructive' });
      return;
    }
    openEscalationDetail(esc);
  };

  const getEscalationBadge = (level) => {
    const configs = {
      operator: { color: 'bg-blue-500', label: t('ops.esc.badgeOperator') },
      manager:  { color: 'bg-orange-500', label: t('ops.esc.badgeManager') },
      admin:    { color: 'bg-red-500', label: t('ops.esc.badgeAdmin') }
    };
    const config = configs[level] || configs.operator;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getLanguageBadge = (language) => (
    <Badge variant="outline">{language === 'english' ? 'English' : language || 'Unknown'}</Badge>
  );

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return formatInTenantTz(date, { dateStyle: 'medium' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>{t('ops.esc.loadingEscalations')}</p>
        </div>
      </div>
    );
  }

  // GOV-7 WS1 Area D — read the server-authoritative buckets directly (see fetchEscalations). "Pending"
  // now correctly surfaces escalations assigned to me that I have not yet accepted (canonical status
  // 'assigned' + my callbacks); "Assigned to me" is the accepted/actively-handled set (receiving_attention).
  const pendingEscalations    = buckets.pending;
  const myEscalations         = buckets.assigned_to_me;
  const resolvedEscalations   = buckets.resolved;

  // Callback reminder derivations (recompute on each tick via nowMs).
  const cbStates = callbacks.map(cb => ({ cb, st: callbackState(cb) }));
  const dueSoonCallbacks = cbStates.filter(x => x.st.key === 'due_soon')
    .sort((a, b) => (a.st.minsToGo ?? 0) - (b.st.minsToGo ?? 0));
  const overdueCount = cbStates.filter(x => x.st.key === 'overdue').length;
  const dueSoonCount = dueSoonCallbacks.length;
  const reminderCount = dueSoonCount + overdueCount;
  const soonest = dueSoonCallbacks[0];

  // DR raw-ID policy: ordinary operational screens must not display raw internal UUIDs. Build a friendly
  // human reference from the best existing authoritative value, in order: (1) human ticket number (TKT-…),
  // (2) conversation human reference [none exists — UUID only], (3) customer name + created date/time,
  // (4) safe fallback "Reference unavailable". Never falls back to a raw conversation/ticket/escalation UUID.
  // Canonical ids remain internal (React keys, action payloads, routing) via viewEscDialog.id.
  const escReference = (esc) => {
    if (!esc) return t('ops.esc.referenceUnavailable', 'Reference unavailable');
    if (esc.ticket_reference) return esc.ticket_reference;                        // 1. human ticket number
    if (esc.customer_name && esc.created_at)                                       // 3. customer + created time
      return `${esc.customer_name} · ${formatTime(esc.created_at)}`;
    return t('ops.esc.referenceUnavailable', 'Reference unavailable');            // 4. safe fallback (never a UUID)
  };

  return (
    <>
    {/* Escalation Detail Dialog */}
    <Dialog open={!!viewEscDialog} onOpenChange={() => setViewEscDialog(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('ops.esc.escalationDetail')}</DialogTitle>
          <DialogDescription>
            {viewEscDialog?.customer_name || t('ops.common.customer')} — {formatTime(viewEscDialog?.created_at)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-semibold text-gray-700">{t('ops.common.customer')}</p>
            <p className="text-gray-600">{viewEscDialog?.customer_name || '—'}</p>
          </div>
          <div>
            <p className="font-semibold text-gray-700">{t('ops.esc.intent')}</p>
            <p className="text-gray-600">{viewEscDialog?.detected_intent || '—'}</p>
          </div>
          <div>
            <p className="font-semibold text-gray-700">{t('common.language')}</p>
            <p className="text-gray-600">{viewEscDialog?.detected_language || '—'}</p>
          </div>
          <div>
            <p className="font-semibold text-gray-700">{t('ops.esc.escalationReason')}</p>
            <p className="text-gray-600">{viewEscDialog?.escalation_reason || viewEscDialog?.reason || '—'}</p>
          </div>
          <div>
            <p className="font-semibold text-gray-700">{t('ops.esc.aiContextSummary')}</p>
            <p className="text-gray-600 bg-gray-50 p-3 rounded">
              {viewEscDialog?.context_summary || t('ops.esc.noContextSummary')}
            </p>
          </div>
          <div>
            <p className="font-semibold text-gray-700">{t('ops.esc.reference', 'Reference')}</p>
            <p className="text-gray-600">{escReference(viewEscDialog)}</p>
          </div>
          <div>
            <p className="font-semibold text-gray-700">{t('ops.common.assignedTo')}</p>
            <p className="text-gray-600">
              {viewEscDialog?.assigned_to
                ? (viewEscDialog?.assigned_to_name
                    || businessStaff.find(s => s.id === viewEscDialog.assigned_to)?.name
                    || t('ops.common.staff', 'Staff Member'))
                : t('ops.common.unassigned')}
            </p>
          </div>
          <div>
            <p className="font-semibold text-gray-700">{t('ops.common.status')}</p>
            <Badge variant="outline" className="capitalize">{viewEscDialog?.status || '—'}</Badge>
          </div>
          {viewEscDialog?.callback_due_time && (
            <div>
              <p className="font-semibold text-gray-700">{t('ops.esc.callbackDue')}</p>
              <p className="text-orange-600 font-mono text-xs">{formatInTenantTz(viewEscDialog.callback_due_time)}</p>
            </div>
          )}
          {viewEscDialog?.session_id && (
            <div>
              <p className="font-semibold text-gray-700 mb-2">{t('ops.esc.voiceTranscript')}</p>
              {transcriptLoading ? (
                <p className="text-gray-400 text-xs">{t('ops.esc.loadingTranscript')}</p>
              ) : transcript && transcript.length > 0 ? (
                <div className="bg-gray-50 rounded p-2 max-h-48 overflow-y-auto space-y-1">
                  {transcript.map((turn, i) => (
                    <div key={i} className={`text-xs ${turn.speaker === 'customer' ? 'text-blue-700' : 'text-gray-700'}`}>
                      <span className="font-semibold capitalize">{turn.speaker || t('ops.common.agent')}: </span>
                      {turn.text || turn.message || ''}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-xs">{t('ops.esc.noTranscript')}</p>
              )}
            </div>
          )}

          {/* ── Call customer directly from escalation ── */}
          {(viewEscDialog?.conversation_id || viewEscDialog?.ticket_id) && (
            <div className="border-t pt-3 mt-1 flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-1 text-green-700 border-green-300 hover:bg-green-50"
                onClick={() => handleCallFromEscalation(viewEscDialog)}
              >
                <Phone className="w-4 h-4" />
                {t('ops.esc.call')} {viewEscDialog?.customer_name || t('ops.common.customer')}
              </Button>
              <span className="text-xs text-gray-400">{t('ops.esc.callHint')}</span>
            </div>
          )}

          {/* ── Conversation Thread — post-escalation operator ↔ customer messages ── */}
          {(viewEscDialog?.conversation_id || viewEscDialog?.ticket_id) && (
            <div className="border-t pt-3 mt-1">
              <p className="font-semibold text-gray-700 mb-2">{t('ops.esc.conversationThread')}</p>
              {convMessagesLoading ? (
                <p className="text-gray-400 text-xs">{t('ops.esc.loadingMessages')}</p>
              ) : convMessages.length > 0 ? (
                <div className="bg-gray-50 rounded p-2 max-h-48 overflow-y-auto space-y-2">
                  {convMessages.map((msg, i) => {
                    const isOperator = msg.sender_type === 'operator' || msg.sender_type === 'staff';
                    const isAI = msg.sender_type === 'ai';
                    const isSystem = msg.sender_type === 'system';
                    return (
                      <div key={msg.id || i} className={`text-xs flex flex-col ${isOperator ? 'items-end' : 'items-start'}`}>
                        <span className={`text-[10px] font-semibold mb-0.5 ${isOperator ? 'text-blue-600' : isAI ? 'text-teal-600' : isSystem ? 'text-gray-400' : 'text-gray-500'}`}>
                          {isOperator ? t('ops.common.you') : isAI ? t('ops.common.aiAgent') : isSystem ? `⚙ ${t('ops.common.system')}` : t('ops.common.customer')}
                        </span>
                        <span className={`rounded px-2 py-1 ${isSystem ? 'w-full text-center italic' : 'max-w-[80%]'} ${isOperator ? 'bg-blue-100 text-blue-800' : isAI ? 'bg-teal-50 text-teal-800' : isSystem ? 'bg-gray-100 text-gray-500 text-[10px]' : 'bg-white text-gray-700 border'}`}>
                          {msg.message || msg.content || ''}
                        </span>
                        {msg.timestamp && (
                          <span className="text-[9px] text-gray-400 mt-0.5">
                            {formatInTenantTz(msg.timestamp, { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400 text-xs">{t('ops.esc.noMessagesYet')}</p>
              )}

              {/* Message compose — only for active escalations (not closed/resolved) */}
              {!['resolved', 'closed'].includes(viewEscDialog?.status) && (
                <div className="mt-2 flex gap-2">
                  <Textarea
                    placeholder={t('ops.esc.typeMessageCustomer')}
                    value={detailMsgText}
                    onChange={(e) => setDetailMsgText(e.target.value)}
                    rows={2}
                    className="flex-1 text-sm resize-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(viewEscDialog, detailMsgText, viewEscDialog.id, true);
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    className="self-end"
                    disabled={!detailMsgText.trim() || detailMsgSending}
                    onClick={() => handleSendMessage(viewEscDialog, detailMsgText, viewEscDialog.id, true)}
                  >
                    {detailMsgSending ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="w-8 h-8 text-blue-600" />
            {t('page.myEscalations.title')}
            {pendingEscalations.length > 0 && (
              <Badge variant="destructive" className="ml-2">{t('ops.esc.pendingBadge', { count: pendingEscalations.length })}</Badge>
            )}
          </h1>
          <p className="text-gray-600 mt-1">{t('page.myEscalations.subtitle')}</p>
        </div>
        <Button onClick={() => { fetchEscalations(); fetchCallbacks(); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('common.refresh')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('ops.common.pending')}</p>
                <p className="text-3xl font-bold text-orange-600">{pendingEscalations.length}</p>
              </div>
              <Clock className="w-10 h-10 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('ops.esc.assignedToMe')}</p>
                <p className="text-3xl font-bold text-blue-600">{myEscalations.length}</p>
              </div>
              <User className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('ops.common.resolved')}</p>
                <p className="text-3xl font-bold text-green-600">{resolvedEscalations.length}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">{t('ops.common.pending')} ({pendingEscalations.length})</TabsTrigger>
          <TabsTrigger value="assigned">{t('ops.esc.assignedToMe')} ({myEscalations.length})</TabsTrigger>
          <TabsTrigger value="callbacks">
            {t('ops.esc.callbacks')} ({callbacks.length})
            {reminderCount > 0 && (
              <span className={`ml-1 font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                ({reminderCount})
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="resolved">{t('ops.common.resolved')} ({resolvedEscalations.length})</TabsTrigger>
          {/* D15 — management-only tenant queues (server-authoritative /escalations/team). Operators
              never see these tabs (defense-in-depth; backend independently 403s the /team route). */}
          {isAdminOrManager && (
            <>
              <TabsTrigger value="team">{t('ops.esc.teamQueue')}</TabsTrigger>
              <TabsTrigger value="unassigned">{t('ops.esc.unassignedQueue')}</TabsTrigger>
            </>
          )}
        </TabsList>

        {/* ── Pending Tab ── */}
        <TabsContent value="pending" className="mt-4">
          {pendingEscalations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>{t('ops.esc.noPendingEsc')}</p>
                <p className="text-sm mt-1">{t('ops.esc.allHandledEfficiently')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4 pr-1">
              {pendingEscalations.map((esc, index) => {
                const escId = esc.id;
                return (
                  <Card key={escId || index} className="border-orange-200">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <MessageSquare className="w-5 h-5" />
                            {esc.customer_name || t('ops.common.customer')}
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-2">
                            {getEscalationBadge(esc.escalated_to)}
                            {getLanguageBadge(esc.detected_language)}
                            <Badge variant="outline">{esc.detected_intent}</Badge>
                            <span className="text-xs text-gray-500">{formatTime(esc.created_at)}</span>
                          </CardDescription>
                        </div>
                        <AlertCircle className="w-6 h-6 text-orange-500" />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">{t('ops.esc.escalationReason')}:</p>
                        <p className="text-sm text-gray-600">{esc.escalation_reason || esc.reason || '—'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">{t('ops.esc.contextSummary')}:</p>
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                          {esc.context_summary || t('ops.esc.noSummaryAvailable')}
                        </p>
                      </div>
                      <div className="flex gap-2 pt-2">
                        {/* Accept & Handle: self-assigns then accepts in one click.
                            Backend requires assigned status; self-assign step runs first if pending.
                            Admin/manager can claim; operators see a clear error from backend. */}
                        <Button
                          className="flex-1"
                          onClick={() => handleAcceptEscalation(esc)}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {t('ops.esc.acceptHandle')}
                        </Button>
                        <Button variant="outline" onClick={() => handleViewEscalation(esc)}>
                          <ArrowRight className="w-4 h-4 mr-2" />
                          {t('ops.esc.viewDetails')}
                        </Button>
                      </div>

                      {/* FIX Issue 4: Staff Select dropdown replaces free-text Input */}
                      {isAdminOrManager && (
                        <div className="flex gap-2 pt-1">
                          <Select
                            value={assignInputs[escId] || ''}
                            onValueChange={(value) => setAssignInputs(prev => ({ ...prev, [escId]: value }))}
                          >
                            <SelectTrigger className="flex-1 text-sm">
                              <SelectValue placeholder={t('ops.esc.selectOperatorAssign')} />
                            </SelectTrigger>
                            <SelectContent>
                              {businessStaff.length === 0 ? (
                                <SelectItem value="_none" disabled>{t('ops.esc.noStaffAvailable')}</SelectItem>
                              ) : (
                                businessStaff.map(s => (
                                  <SelectItem key={s.id} value={s.id}>
                                    {s.name || s.email} — {s.role}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!assignInputs[escId] || assignInputs[escId] === '_none'}
                            onClick={() => handleAssignEscalation(esc, assignInputs[escId])}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            {t('ops.common.assign')}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Assigned to Me Tab ── */}
        <TabsContent value="assigned" className="mt-4">
          {myEscalations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <User className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>{t('ops.esc.noEscAssigned')}</p>
                <p className="text-sm mt-1">{t('ops.esc.noEscAssignedHelp')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4 pr-1">
              {myEscalations.map((esc, index) => {
                const escId = esc.id;
                const _normStatus = (esc.status || '').toLowerCase().replace(/[\s-]+/g, '_');
                const canAccept = _normStatus === 'assigned';
                const canResolve = ['receiving_attention', 'in_progress', 'assigned'].includes(_normStatus);
                return (
                  <Card key={escId || index} className="border-blue-200">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <MessageSquare className="w-5 h-5" />
                            {esc.customer_name || t('ops.common.customer')}
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-2">
                            {getEscalationBadge(esc.escalated_to)}
                            {getLanguageBadge(esc.detected_language)}
                            <Badge variant="outline" className="capitalize">{esc.status}</Badge>
                            <span className="text-xs text-gray-500">{formatTime(esc.created_at)}</span>
                          </CardDescription>
                        </div>
                        <CheckCircle className="w-6 h-6 text-blue-500" />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">{t('ops.esc.escalationReason')}:</p>
                        <p className="text-sm text-gray-600">{esc.escalation_reason || esc.reason || '—'}</p>
                      </div>
                      <div className="flex gap-2 pt-2">
                        {canAccept && (
                          <Button
                            className="flex-1"
                            onClick={() => handleAcceptEscalation(esc)}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {t('ops.esc.acceptHandle')}
                          </Button>
                        )}
                        {canResolve && (
                          <Button
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => openResolveDialog(esc)}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {t('ops.common.resolve')}
                          </Button>
                        )}
                        {!canAccept && !canResolve && (
                          <Button className="flex-1" disabled>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {t('ops.common.inProgress')}
                          </Button>
                        )}
                        <Button variant="outline" onClick={() => handleViewEscalation(esc)}>
                          <ArrowRight className="w-4 h-4 mr-2" />
                          {t('ops.esc.viewDetails')}
                        </Button>
                      </div>

                      {/* Inline message compose — shown when receiving_attention so operator can message without opening dialog */}
                      {_normStatus === 'receiving_attention' && (esc.conversation_id || esc.ticket_id) && (
                        <div className="mt-3 border-t pt-3">
                          <p className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {t('ops.esc.messageCustomer')}
                          </p>
                          <div className="flex gap-2">
                            <Textarea
                              placeholder={t('ops.esc.typeMessageEnter')}
                              value={msgTexts[escId] || ''}
                              onChange={(e) => setMsgTexts(prev => ({ ...prev, [escId]: e.target.value }))}
                              rows={2}
                              className="flex-1 text-sm resize-none"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSendMessage(esc, msgTexts[escId] || '', escId, false);
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              className="self-end"
                              disabled={!(msgTexts[escId] || '').trim() || msgSending[escId]}
                              onClick={() => handleSendMessage(esc, msgTexts[escId] || '', escId, false)}
                            >
                              {msgSending[escId] ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Callbacks Tab (canonical db.callbacks; names not raw IDs) ── */}
        <TabsContent value="callbacks" className="mt-4">
          {callbacks.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-500">{t('ops.esc.noScheduledCallbacks')}</CardContent></Card>
          ) : (
            <div className="space-y-4">
              {/* Reminder banner: nearest callback entering the 5-minute window. */}
              {soonest && (
                <div className="flex items-center gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <Clock className="w-4 h-4 shrink-0" />
                  <span>
                    {t('ops.esc.cbDueSoonMsg')}: <span className="font-semibold">{soonest.cb.customer_display_name || soonest.cb.customer_name || t('ops.common.customer')}</span>
                    {soonest.st.minsToGo != null ? ` (${Math.max(0, Math.ceil(soonest.st.minsToGo))}m)` : ''}
                  </span>
                </div>
              )}
              {cbStates.map(({ cb, st }) => {
                const badge = CB_BADGE[st.key] || CB_BADGE.scheduled;
                const cardCls = st.key === 'overdue' ? 'border-red-400'
                  : st.key === 'due_soon' ? 'border-amber-400 ring-1 ring-amber-300'
                  : 'border-orange-300';
                return (
                <Card key={cb.id} className={cardCls}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        {/* PRIMARY label = customer display name (never a raw id) */}
                        <p className="font-semibold">{cb.customer_display_name || t('ops.common.customer')}</p>
                        {cb.notes && <p className="text-sm text-gray-600">{cb.notes}</p>}
                        <p className="text-xs mt-1 text-orange-600 font-mono">
                          {t('ops.esc.scheduledFor')}: {cb.scheduled_date} {cb.scheduled_time}
                        </p>
                        <p className="text-xs mt-1 text-gray-500">
                          {t('ops.esc.assignedTo')}: {cb.callback_assignee_display_name || t('ops.common.staff', 'Staff Member')}
                          {cb.business_display_name ? ` · ${cb.business_display_name}` : ''}
                        </p>
                        {cb.customer_phone && (
                          <p className="text-xs mt-1 text-gray-400 font-mono">{cb.customer_phone}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <Badge variant={badge.variant} className={`capitalize ${badge.cls}`}>
                          {t(badge.label)}
                        </Badge>
                        {/* Real VoIP/Twilio call via the shared /conversations/{id}/call route. */}
                        <Button
                          size="sm"
                          onClick={() => handleCallFromCallback(cb)}
                          disabled={callingCbId === cb.id}
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          {callingCbId === cb.id ? t('ops.common.startingCall') : t('ops.common.callNow')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Resolved Tab ── */}
        <TabsContent value="resolved" className="mt-4">
          {resolvedEscalations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                <p>{t('ops.esc.noResolvedYet')}</p>
                <p className="text-sm mt-1">{t('ops.esc.completedAppearHere')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4 pr-1">
              {resolvedEscalations.map((esc, index) => {
                const escId = esc.id;
                return (
                  <Card key={escId || index} className="border-green-200">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <MessageSquare className="w-5 h-5" />
                            {esc.customer_name || t('ops.common.customer')}
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="capitalize">{esc.status}</Badge>
                            <span className="text-xs text-gray-500">{formatTime(esc.created_at)}</span>
                          </CardDescription>
                        </div>
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Button variant="outline" onClick={() => handleViewEscalation(esc)}>
                        <ArrowRight className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── D15 Management: Team + Unassigned queues (server-authoritative /escalations/team) ── */}
        {isAdminOrManager && ['team', 'unassigned'].map((scopeTab) => (
          <TabsContent key={scopeTab} value={scopeTab} className="mt-4">
            {teamLoading ? (
              <Card><CardContent className="py-12 text-center text-gray-500">
                <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                <p>{t('ops.esc.loadingQueue')}</p>
              </CardContent></Card>
            ) : teamEscalations.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>{scopeTab === 'unassigned' ? t('ops.esc.noUnassigned') : t('ops.esc.noTeamEsc')}</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-4 pr-1">
                {teamEscalations.map((esc, index) => {
                  const escId = esc.id;
                  const st = esc.status;
                  const canReassign = st === 'assigned' || st === 'receiving_attention';
                  const canAssign = !esc.assigned_to_name && (st === 'pending' || st === 'pending_callback');
                  const canClose = st === 'resolved';
                  const busy = actioningId === escId;
                  return (
                    <Card key={escId || index} className="border-indigo-200 min-w-0">
                      <CardHeader>
                        <div className="flex justify-between items-start gap-2 min-w-0">
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-base flex items-center gap-2 min-w-0">
                              <MessageSquare className="w-4 h-4 shrink-0" />
                              <span className="truncate">{esc.customer_name || t('ops.common.customer')}</span>
                            </CardTitle>
                            <CardDescription className="flex flex-wrap items-center gap-2 mt-2">
                              {esc.ticket_reference && <Badge variant="outline">{esc.ticket_reference}</Badge>}
                              {getEscalationBadge(esc.escalated_to)}
                              {esc.detected_intent && <Badge variant="outline">{esc.detected_intent}</Badge>}
                              <Badge variant="secondary">{String(st || '').replace(/_/g, ' ')}</Badge>
                              <span className="text-xs text-gray-500">
                                {t('ops.esc.assignedLabel')}: {esc.assigned_to_name || t('ops.esc.unassigned')}
                              </span>
                              <span className="text-xs text-gray-500">{formatTime(esc.created_at)}</span>
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                          {esc.context_summary || esc.escalation_reason || esc.reason || t('ops.esc.noSummaryAvailable')}
                        </p>
                        <div className="flex flex-wrap gap-2 items-center">
                          <Button variant="outline" size="sm" onClick={() => handleViewEscalation(esc)}>
                            <ArrowRight className="w-4 h-4 mr-2" />{t('ops.esc.viewDetails')}
                          </Button>
                          {canClose && (
                            <Button size="sm" disabled={busy} className="bg-gray-700 hover:bg-gray-800"
                              onClick={() => handleCloseEscalation(esc)}>
                              <CheckCircle className="w-4 h-4 mr-2" />{t('ops.esc.close')}
                            </Button>
                          )}
                        </div>
                        {(canAssign || canReassign) && (
                          <div className="flex gap-2 min-w-0">
                            <Select
                              value={(canReassign ? reassignInputs[escId] : assignInputs[escId]) || ''}
                              onValueChange={(v) => canReassign
                                ? setReassignInputs(prev => ({ ...prev, [escId]: v }))
                                : setAssignInputs(prev => ({ ...prev, [escId]: v }))}
                            >
                              <SelectTrigger className="flex-1 text-sm min-w-0">
                                <SelectValue placeholder={t('ops.esc.selectOperatorAssign')} />
                              </SelectTrigger>
                              <SelectContent>
                                {businessStaff.length === 0 ? (
                                  <SelectItem value="_none" disabled>{t('ops.esc.noStaffAvailable')}</SelectItem>
                                ) : businessStaff.map(s => (
                                  <SelectItem key={s.id} value={s.id}>{s.name || s.email} — {s.role}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button variant="outline" size="sm" disabled={busy ||
                              !((canReassign ? reassignInputs[escId] : assignInputs[escId]) || '').trim() ||
                              (canReassign ? reassignInputs[escId] : assignInputs[escId]) === '_none'}
                              onClick={() => canReassign
                                ? handleReassignEscalation(esc, reassignInputs[escId])
                                : handleAssignEscalation(esc, assignInputs[escId])}>
                              <UserPlus className="w-4 h-4 mr-2" />
                              {canReassign ? t('ops.esc.reassign') : t('ops.common.assign')}
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>

    {/* Resolve Dialog with Solution Bank submission option */}
    <Dialog open={!!resolveDialog} onOpenChange={(open) => { if (!open) setResolveDialog(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('ops.esc.resolveEscalation')}</DialogTitle>
          <DialogDescription>
            {t('ops.esc.resolveEscDesc')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label htmlFor="resolve-notes">{t('ops.esc.resolutionNotes')}</Label>
            <Textarea
              id="resolve-notes"
              placeholder={t('ops.common.describeResolution')}
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              rows={4}
              className="mt-1"
            />
          </div>
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <Checkbox
              id="submit-solution-bank"
              checked={submitToSolutionBank}
              onCheckedChange={setSubmitToSolutionBank}
              disabled={!resolveNotes.trim()}
            />
            <div>
              <Label htmlFor="submit-solution-bank" className="font-medium cursor-pointer">
                {t('ops.esc.submitToSolutionBank')}
              </Label>
              <p className="text-xs text-amber-700 mt-1">
                {t('ops.esc.approveBeforeAi')}
              </p>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setResolveDialog(null)}>{t('common.cancel')}</Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={handleResolveEscalation}
              disabled={!resolveNotes.trim()}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {t('ops.common.resolve')}{submitToSolutionBank ? t('ops.esc.submitForReviewSuffix') : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default EscalationManager;
